import {
  Component,
  HostListener,
  inject,
  viewChild,
  OnInit,
} from '@angular/core';
import {
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { IdleService } from './core/services/idle.service';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { BreadcrumbComponent } from './shared/components/breadcrumb/breadcrumb.component';
import { ToastModule } from 'primeng/toast';
import { ApplicationConstants } from './shared/constants/ApplicationConstants';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { LogoutService } from './core/services/logout.service';
import { RoleService } from './shared/services/role.service';
import { appInsights } from './app.config';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs';
import { UserManagementService } from './shared/services/user-management.service';
import { LeftNavigationComponent } from './components/left-navigation/left-navigation.component'; // Adjust path if needed
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClientSelectionDialogComponent } from './shared/components/client-selection-dialog/client-selection-dialog.component';
import { Tenant } from './shared/interfaces/Tenant';
import { ClientService } from './shared/services/client-service';
import { ClientContextService } from './core/services/client-context.service';
import { CommonService } from '@ao/shared/services/common.service';
import { EXCLUDED_ROUTES } from './shared/constants/excluded-routes';
import { SecureStorageService } from './core/services/secure-storage.service';
import { AuthReadyService } from './core/services/auth-ready.service';
import { WorkspaceFilterStateService } from './shared/services/workspace-filter-state.service';

const initialUrl = window.location.pathname;
if (initialUrl === '/' || initialUrl === '/login') {
  sessionStorage.setItem('directLogin', 'true');
} else {
  sessionStorage.removeItem('directLogin');
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    SpinnerComponent,
    HeaderComponent,
    FooterComponent,
    ToastModule,
    BreadcrumbComponent,
    ConfirmDialogComponent,
    LeftNavigationComponent,
  ],
  providers: [DialogService],
})
export class AppComponent implements OnInit {
  private dialogRef: DynamicDialogRef | null = null;
  private router = inject(Router);
  private idleService = inject(IdleService);
  private secureSessionStorageService = inject(SecureStorageService);
  private logoutService = inject(LogoutService);
  private roleService = inject(RoleService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private userManagementService = inject(UserManagementService);
  private dialogService = inject(DialogService);
  private clientService = inject(ClientService);
  private clientContext = inject(ClientContextService);
  private commonService = inject(CommonService);
  private secureStorage = inject(SecureStorageService);
  private authReadyService = inject(AuthReadyService);
  private filterStateService = inject(WorkspaceFilterStateService);
  isLoginPage = false;
  isUnauthorizedPage = false;
  displayIdleDialog = false;
  roleName: string | undefined;
  readonly confirmDialog =
    viewChild.required<ConfirmDialogComponent>('confirmDialog');
  isRouteLoading = false;

  get showBreadcrumb(): boolean | any {
    const currentRoute = this.router.url;
    const hasValidRoute =
      currentRoute &&
      currentRoute !== '/' &&
      currentRoute !== '/login' &&
      currentRoute !== '/no-auth' &&
      currentRoute !== '/cookie-settings' &&
      currentRoute !== '/terms-of-use' &&
      currentRoute !== '/privacy-notice' &&
      currentRoute !== '/cookie-policies' &&
      currentRoute !== '/error';

    return (
      !this.isLoginPage &&
      !this.isUnauthorizedPage &&
      hasValidRoute &&
      (!this.isRouteLoading || this.hasBreadcrumbData())
    );
  }
  get shouldShowClientSelection(): boolean {
    const currentRoute = this.router.url;
    const excludedRoutes = EXCLUDED_ROUTES;
    return (
      !this.isLoginPage &&
      !this.isUnauthorizedPage &&
      !excludedRoutes.includes(currentRoute) &&
      !this.isRouteLoading
    );
  }
  /** True only for an actual F5/reload, not for a fresh tab, typed URL, or deep link. */
  private isBrowserReload(): boolean {
    const [navigationEntry] = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    return navigationEntry?.type === 'reload';
  }

  private hasBreadcrumbData(): boolean {
    // Check if current route or any parent has breadcrumb data
    let route = this.router.routerState.root;
    while (route) {
      if (route.snapshot.data?.['breadcrumb']) {
        return true;
      }
      if (route.children.length > 0) {
        route = route.children[0];
      } else {
        break;
      }
    }
    return false;
  }

  constructor() {
    // AppComponent only bootstraps once per real page load, so this only fires on a genuine reload.
    if (this.isBrowserReload()) {
      this.filterStateService.clearAll();
    }

    this.idleService.idleTimeout.subscribe(isIdle => {
      if (isIdle) {
        this.showIdlePopup();
      }
    });
    this.msalBroadcastService.inProgress$
      .pipe(filter((status: string) => status === InteractionStatus.None))
      .subscribe(() => {
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount) {
          const loginStatusUpdated =
            this.secureStorage.getItem('loginStatusUpdated');
          const currentUserOid =
            activeAccount.localAccountId || activeAccount.homeAccountId;

          if (!loginStatusUpdated || loginStatusUpdated !== currentUserOid) {
            // Login-history record not yet written for this session.
            // Update it first, then unblock TenantGuard and navigate — this
            // eliminates the race where getClients() reaches the backend before
            // IsLoggedin=true is committed, causing a 401 → /no-auth redirect.
            this.userManagementService.updateLoginStatus(true).subscribe({
              next: () => {
                this.secureStorage.setItem(
                  'loginStatusUpdated',
                  currentUserOid
                );
                this.authReadyService.markReady();
                this.navigateToDashboardIfDirectLogin();
              },
              error: () => {
                // Even on API error, unblock TenantGuard so the app
                // can still proceed (getClients will surface its own error).
                this.authReadyService.markReady();
                this.navigateToDashboardIfDirectLogin();
              },
            });
          } else {
            // Login status already recorded this session — unblock immediately.
            this.authReadyService.markReady();
            this.navigateToDashboardIfDirectLogin();
          }
        }
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isRouteLoading = true;
        this.isLoginPage = event.url === '/';
        this.isUnauthorizedPage = event.url === '/no-auth';
        // if route matches '/project-management/project-archival', and if it is a reload navigate to '/project-management'
        // if (event.url.startsWith('/project-management/project-archival') && event.id === 1) {
        //   this.router.navigate(['/project-management'], {
        //     replaceUrl: true,
        //   });
        // }
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.isRouteLoading = false;
      }

      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects || event.url;
        if (url === '/dashboard' || url === '/') {
          sessionStorage.removeItem('isDeeplink');
        }
        appInsights.trackPageView({ name: event.urlAfterRedirects });

        const isDeeplink = sessionStorage.getItem('isDeeplink') === 'true';

        if (isDeeplink && !this.clientContext.getCurrentClient()) {
          this.handleDeepLinkClientContext();
        }

        if (
          this.shouldShowClientSelection &&
          !this.clientContext.getCurrentClient() &&
          !isDeeplink
        ) {
          this.openClientSelectionDialog();
        }
        
      }
    });

    // Subscribe to client context changes and open dialog if needed
    this.clientContext.clientChanges$.subscribe(client => {
      if (this.shouldShowClientSelection && !client) {
        this.openClientSelectionDialog();
      }
    });

    this.roleService.roleDisplayName$.subscribe(roleName => {
      this.roleName = roleName;
    });
  }

  ngOnInit(): void {
    this.commonService.switchClientRequested$.subscribe(() => {
      if (this.shouldShowClientSelection) {
        this.openClientSelectionDialog();
      }
    });
    this.handleDeepLinkClientContext();
    this.checkAndHandleAuthentication();
  }

  private handleDeepLinkClientContext(): void {
    const isDeeplink = sessionStorage.getItem('isDeeplink') === 'true';
    const tenantIdRaw =
      sessionStorage.getItem('TenantId') || sessionStorage.getItem('tenantId');
    const tenantId = tenantIdRaw ? JSON.parse(tenantIdRaw) : null;
    const currentClient = this.clientContext.getCurrentClient();

    if (isDeeplink && tenantId && !currentClient) {
      this.clientService.getClients().subscribe(clients => {
        const matched = clients.find(c => c.tenantGuid === tenantId);
        if (matched) {
          this.clientContext.setCurrentClient(matched);
          this.secureStorage.setItem(
            ApplicationConstants.SessionKeyConstants.AiEnabled,
            String(Boolean(matched.aiEnabled))
          );
        }
      });
    }
  }

  /**
   * Opens the client selection dialog. This method is public so it can be
   * called from child components like the header/navigation.
   */
  public openClientSelectionDialog(): void {
    if (this.dialogRef) {
      return;
    }
    const dialogRef = this.dialogService.open(ClientSelectionDialogComponent, {
      header: 'Select Client',
      width: '600px',
      height: '350px',
      closable: false,
      modal: true,
      closeOnEscape: false,
    });
    if (!dialogRef) {
      return;
    }
    this.dialogRef = dialogRef;

    dialogRef.onClose.subscribe((client: Tenant | undefined) => {
      if (client) {
        this.clientContext.setCurrentClient(client);
      }
      // Always reset dialogRef so dialog can be opened again
      this.dialogRef = null;
    });
  }

  showIdlePopup() {
    this.displayIdleDialog = true;
    this.confirmDialog().confirm();
  }

  continueSession() {
    this.idleService.continueSession();
    this.displayIdleDialog = false;
  }

  logout() {
    this.logoutService.logout();
    this.displayIdleDialog = false;
  }

  private navigateToDashboardIfDirectLogin(): void {
    const currentUrl = this.router.url;
    const directLogin = sessionStorage.getItem('directLogin');
    if (directLogin && (currentUrl === '/' || currentUrl === '/login')) {
      sessionStorage.removeItem('directLogin');
      this.router.navigate(['/dashboard'], { replaceUrl: true });
    }
  }

  checkAndHandleAuthentication() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      const loginStatusUpdated =
        this.secureStorage.getItem('loginStatusUpdated');
      const currentUserOid =
        activeAccount.localAccountId || activeAccount.homeAccountId;

      if (!loginStatusUpdated || loginStatusUpdated !== currentUserOid) {
        //this.userManagementService.updateLoginStatus(true).subscribe({
        //  next: () => {
        //    this.secureStorage.setItem('loginStatusUpdated', currentUserOid);
        //  },
        //  error: () => {},
        //});
      }

      setTimeout(() => {
        const currentUrl = this.router.url;
        const directLogin = sessionStorage.getItem('directLogin');
        if (directLogin && (currentUrl === '/' || currentUrl === '/login')) {
          sessionStorage.removeItem('directLogin');
          this.router.navigate(['/dashboard'], { replaceUrl: true });
        }
      }, 100);
    }
  }

  // sessionStorage is auto-cleared on real tab close; clearing it on beforeunload
  // wrongly wipes user access when the user cancels a navigation prompt.
  @HostListener('window:pagehide', ['$event'])
  pageHideHandler(event: PageTransitionEvent): void {
    if (event.persisted) return;
    this.secureSessionStorageService.removeItem(
      ApplicationConstants.SessionKeyConstants.UserAccessDetails
    );
  }
}
